import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowRight, Receipt } from "@untitledui/icons";
import {
  learningGetPurchaseReceipt,
  useLearningListPurchases,
} from "@/services/api/learning-purchases/learning-purchases";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { formatCoursePrice, toNum } from "@/utils/course-formatters";
import { formatSeatCount, seatOrderStatusLabel } from "@/features/seat-orders/seatOrders";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import type { PurchaseHistoryItem } from "@/types/api";

export const Route = createFileRoute("/_app/my-purchases")({
  // Purchase history is hidden while the payments feature flag is off (the API 404s it too).
  beforeLoad: async () => {
    const { payments } = await fetchFeatureFlags();
    if (!payments) throw redirect({ to: "/my-learning" });
  },
  component: MyPurchases,
});

function MyPurchases() {
  // Asked afresh on each visit and when the tab regains focus: an admin may have moved an invoice order on (LMS-151).
  const { data, isLoading, isError } = useLearningListPurchases({
    query: { staleTime: 0, refetchOnWindowFocus: true },
  });
  const purchases = data?.items ?? [];

  // Track the receipt action per row so only the activated purchase shows a spinner / error.
  const [receiptPendingId, setReceiptPendingId] = useState<string | null>(null);
  const [receiptErrorId, setReceiptErrorId] = useState<string | null>(null);

  const handleViewReceipt = async (purchaseId: string) => {
    setReceiptErrorId(null);
    setReceiptPendingId(purchaseId);
    try {
      const { receiptUrl } = await learningGetPurchaseReceipt(purchaseId);
      window.open(receiptUrl, "_blank", "noopener,noreferrer");
    } catch {
      setReceiptErrorId(purchaseId);
    } finally {
      setReceiptPendingId(null);
    }
  };

  return (
    <main className="mx-auto max-w-[1280px] px-8 py-8">
      <h1 className="text-arc-gray-900 mb-2 text-4xl font-semibold">My Purchases</h1>
      <p className="text-arc-gray-600 mb-8 text-base">
        A record of the courses and seats you've bought, with a receipt for each card payment.
      </p>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-arc-gray-100 h-24 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : isError ? (
        <div className="text-arc-error-600 py-8 text-center">
          Failed to load your purchases. Please try again.
        </div>
      ) : purchases.length === 0 ? (
        <EmptyState>
          <EmptyState.Header>
            <EmptyState.FeaturedIcon icon={Receipt} color="gray" />
          </EmptyState.Header>
          <EmptyState.Content>
            <EmptyState.Title>No purchases yet</EmptyState.Title>
            <EmptyState.Description>You haven't bought any courses yet.</EmptyState.Description>
          </EmptyState.Content>
        </EmptyState>
      ) : (
        <ul className="divide-arc-gray-200 border-arc-gray-200 divide-y rounded-2xl border">
          {purchases.map((purchase) => (
            <li
              key={purchase.purchaseId}
              className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-arc-gray-900 text-lg font-semibold">
                    {purchase.courseTitle}
                  </span>
                  {/* A booking cancelled with a refund (LMS-186), or a seat order refunded when
                      its session was cancelled (LMS-192). */}
                  {purchase.isRefunded && (
                    <Badge type="pill-color" size="sm" color="gray">
                      Refunded
                    </Badge>
                  )}
                  {/* A seat order paid by invoice (LMS-151) says where it stands. */}
                  {isInvoiceOrder(purchase) && (
                    <Badge
                      type="pill-color"
                      size="sm"
                      color={invoiceStatusColor(purchase.orderStatus)}
                    >
                      {seatOrderStatusLabel(purchase.orderStatus)}
                    </Badge>
                  )}
                </span>
                <span className="text-arc-gray-600 text-sm">
                  {/* Seats on a Restricted learning path (LMS-198). */}
                  {purchase.learningPathId ? "Learning path · " : ""}
                  {purchase.seatOrderId ? `${formatSeatCount(toNum(purchase.quantity))} · ` : ""}
                  {formatCoursePrice(purchase.amountPence, purchase.currency)} ·{" "}
                  {isInvoiceOrder(purchase) ? "Pay by invoice · " : ""}
                  {isDiscountCodeOrder(purchase) ? "Discount code · " : ""}
                  {isFreeCourseOrder(purchase)
                    ? purchase.learningPathId
                      ? "Free learning path · "
                      : "Free course · "
                    : ""}
                  {format(new Date(purchase.purchasedAt), "d MMMM yyyy")}
                  {/* The PO number of an invoice order (LMS-151) or a card order (LMS-193). */}
                  {purchase.poNumber ? ` · PO ${purchase.poNumber}` : ""}
                </span>
                {receiptErrorId === purchase.purchaseId ? (
                  <span className="text-arc-error-600 text-sm" role="alert">
                    Receipt unavailable. Please try again later.
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* A seat order (LMS-140) opens on its own page. */}
                {purchase.seatOrderId && (
                  <Button
                    color="secondary"
                    size="sm"
                    href={`/my-purchases/seat-orders/${purchase.seatOrderId}`}
                    iconTrailing={ArrowRight}
                    aria-label={`View order: ${formatSeatCount(toNum(purchase.quantity))} on ${purchase.courseTitle}`}
                  >
                    View order
                  </Button>
                )}
                {/* A refunded purchase, an invoice order (LMS-151) or a £0 discount code order (LMS-187)
                    has no receipt to open. */}
                {!purchase.isRefunded &&
                  !isInvoiceOrder(purchase) &&
                  !isDiscountCodeOrder(purchase) &&
                  !isFreeCourseOrder(purchase) && (
                    <Button
                      color="secondary"
                      size="sm"
                      iconLeading={Receipt}
                      onClick={() => handleViewReceipt(purchase.purchaseId)}
                      isLoading={receiptPendingId === purchase.purchaseId}
                      isDisabled={receiptPendingId === purchase.purchaseId}
                    >
                      View receipt
                    </Button>
                  )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

/** A seat order paid by invoice (LMS-151): it has no card payment, and so no receipt. */
const isInvoiceOrder = (purchase: PurchaseHistoryItem) => purchase.paymentMethod === "Invoice";

/** A £0 seat order a discount code paid for (LMS-187): there was nothing to pay, so no receipt. */
const isDiscountCodeOrder = (purchase: PurchaseHistoryItem) =>
  purchase.paymentMethod === "DiscountCode";

/**
 * A £0 seat order on a free Restricted course (LMS-196) or learning path (LMS-198): there was nothing to pay, so no
 * receipt.
 */
const isFreeCourseOrder = (purchase: PurchaseHistoryItem) => purchase.paymentMethod === "Free";

/** Paid reads as done, Cancelled as over, and the rest as still to do. */
function invoiceStatusColor(status: PurchaseHistoryItem["orderStatus"]) {
  if (status === "Paid") return "success" as const;
  if (status === "Cancelled") return "gray" as const;
  return "warning" as const;
}
